import React from 'react'
import { Link } from 'react-router-dom';
import { Util } from '../fasades/util';

export class StreamingGuide extends React.Component {
    render() {
        return (
            <>
                <h1>Streaming Guide</h1>
                <p>
                    Streaming allows you to receive responses from the TigerCity.ai API incrementally as they are generated, 
                    rather than waiting for the complete response. This provides a better user experience with real-time feedback 
                    and faster perceived response times.
                </p>

                <h2>How Streaming Works</h2>
                <p>
                    When you enable streaming by setting <code>stream: true</code> in your API request, the server uses 
                    <strong> HTTP chunked transfer encoding</strong> to send the response in multiple parts. Each chunk is a 
                    JSON object that represents a different part of the generation process.
                </p>

                <h3>Request Format</h3>
                <p>
                    To enable streaming, simply add <code>"stream": true</code> to your request body. For example:
                </p>
                <pre><code>{`{
  "model": "Llama-3.1-8b",
  "messages": [
    {
      "role": "user",
      "content": "Why is the sky blue?"
    }
  ],
  "stream": true
}`}</code></pre>

                <h3>Response Format</h3>
                <p>
                    The streaming response consists of multiple JSON chunks, each sent as a separate line. Each chunk is a JSON object 
                    with a <code>type</code> field that indicates what kind of data it contains:
                </p>

                <h4>Chunk Types</h4>
                <ul>
                    <li>
                        <strong>Info chunks</strong> (<code>type: "info"</code>) - Status messages during model loading, such as 
                        "Loading model" or "Loading tokenizer". These provide feedback about the initialization process.
                    </li>
                    <li>
                        <strong>Delta chunks</strong> (<code>type: "delta"</code>) - Incremental text tokens as they are generated. 
                        Each delta chunk contains a small piece of the response text. You accumulate these to build the complete message.
                    </li>
                    <li>
                        <strong>Stop chunks</strong> (<code>type: "stop"</code>) - The final chunk indicating that generation is complete. 
                        This includes the stop reason (e.g., "end_turn", "max_tokens") and usage statistics (duration, token counts).
                    </li>
                    <li>
                        <strong>Error chunks</strong> (<code>type: "error"</code>) - Error messages when something goes wrong during processing. 
                        These indicate that an error occurred and generation cannot continue.
                    </li>
                </ul>

                <h3>Example Streaming Response</h3>
                <p>Here's what a typical streaming response looks like:</p>
                <pre><code>{`{"type": "info", "message": "Loading model llama-3.1-8b"}
{"type": "info", "message": "Loading tokenizer"}
{"type": "delta", "delta": "The"}
{"type": "delta", "delta": " sky"}
{"type": "delta", "delta": " appears"}
{"type": "delta", "delta": " blue"}
{"type": "delta", "delta": " because"}
{"type": "stop", "stop_reason": "end_turn", "usage": {
  "duration_ms": 1396,
  "input_tokens": 33,
  "output_tokens": 35
}}`}</code></pre>

                <h2>Processing Streaming Responses</h2>
                <p>
                    To process a streaming response, you need to:
                </p>
                <ol>
                    <li>Read the response body as a stream (not as JSON)</li>
                    <li>Parse each line as a separate JSON object</li>
                    <li>Handle each chunk type appropriately:
                        <ul>
                            <li>Display info messages to show progress</li>
                            <li>Append delta text to build the complete response</li>
                            <li>Extract usage statistics from the stop chunk</li>
                            <li>Handle error messages and stop processing if an error chunk is received</li>
                        </ul>
                    </li>
                </ol>

                <h3>JavaScript/TypeScript Example</h3>
                <pre><code>{`const response = await fetch('${Util.getTigerAPIUrl()}v1/generate_response', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': \`Bearer \${process.env.TIGER_API_KEY}\`
  },
  body: JSON.stringify({
    model: 'Llama-3.1-8b',
    messages: [{ role: 'user', content: 'Hello!' }],
    stream: true
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();
let buffer = '';

while (true) {
  const { value, done } = await reader.read();
  if (done) break;
  
  buffer += decoder.decode(value, { stream: true });
  const lines = buffer.split('\\n');
  buffer = lines.pop() || ''; // Keep incomplete line in buffer
  
  for (const line of lines) {
    if (line.trim()) {
      const chunk = JSON.parse(line);
      
      if (chunk.type === 'info') {
        console.log('Info:', chunk.message);
      } else if (chunk.type === 'delta') {
        process.stdout.write(chunk.delta); // Print incrementally
      } else if (chunk.type === 'stop') {
        console.log('\\nGeneration complete:', chunk.usage);
      } else if (chunk.type === 'error') {
        console.error('Error:', chunk.message);
        break; // Stop processing on error
      }
    }
  }
}`}</code></pre>

                <h3>Python Example</h3>
                <pre><code>{`import requests
import json
import os
import sys

response = requests.post(
    ${Util.getTigerAPIUrl()}v1/generate_response',
    headers={
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {os.environ["TIGER_API_KEY"]}'
    },
    json={
        'model': 'Llama-3.1-8b',
        'messages': [{'role': 'user', 'content': 'Hello!'}],
        'stream': True
    },
    stream=True
)

# Check if request was successful
if response.status_code != 200:
    print(f'HTTP Error: {response.status_code} - {response.text}', file=sys.stderr)
    sys.exit(1)

try:
    for line in response.iter_lines():
        if line:
            try:
                chunk = json.loads(line)

                if chunk.get('type') == 'info':
                    print(f'Info: {chunk["message"]}')
                elif chunk.get('type') == 'delta':
                    print(chunk['delta'], end='', flush=True)
                elif chunk.get('type') == 'stop':
                    print(f'\\nGeneration complete: {chunk["usage"]}')
                elif chunk.get('type') == 'error':
                    print(f'Error: {chunk["message"]}', file=sys.stderr)
                    break  # Stop processing on error
            except json.JSONDecodeError:
                print(f'Error: Failed to parse JSON chunk: {line}', file=sys.stderr)
                continue
except requests.exceptions.RequestException as e:
    print(f'Request error: {e}', file=sys.stderr)
    sys.exit(1)`}</code></pre>

                <h2>Benefits of Streaming</h2>
                <ul>
                    <li>
                        <strong>Faster perceived response time</strong> - Users see text appearing immediately rather than waiting 
                        for the entire response
                    </li>
                    <li>
                        <strong>Better user experience</strong> - Real-time feedback makes the application feel more responsive
                    </li>
                    <li>
                        <strong>Progress visibility</strong> - Info chunks provide insight into what's happening during model loading
                    </li>
                    <li>
                        <strong>Early cancellation</strong> - You can stop processing if needed, though the server will continue 
                        generating until completion
                    </li>
                </ul>

                <h2>When to Use Streaming</h2>
                <p>
                    Use streaming when:
                </p>
                <ul>
                    <li>Building interactive chat interfaces where users expect to see text appear in real-time</li>
                    <li>You want to provide progress feedback during long-running operations</li>
                    <li>You need to process responses incrementally rather than all at once</li>
                </ul>
                <p>
                    Use non-streaming when:
                </p>
                <ul>
                    <li>You need the complete response before processing it</li>
                    <li>You're making automated API calls where real-time display isn't important</li>
                    <li>You prefer simpler error handling with a single response object</li>
                </ul>

                <h2>Supported Endpoints</h2>
                <p>
                    Streaming is currently supported for:
                </p>
                <ul>
                    <li>
                        <Link to="/apiref/generate-response">Generate Response</Link> - Stream text generation as it happens
                    </li>
                    <li>
                        <Link to="/apiref/train">Train</Link> - Stream training progress and performance metrics
                    </li>
                </ul>

                <h2>Error Handling</h2>
                <p>
                    When streaming, errors can occur at any point. Make sure to:
                </p>
                <ul>
                    <li>Check the HTTP status code before starting to read the stream</li>
                    <li>Handle JSON parsing errors gracefully (malformed chunks)</li>
                    <li>Implement timeout handling for streams that don't complete</li>
                    <li>Validate chunk structure before accessing fields</li>
                </ul>

                <p>
                    For more details on specific endpoints, see the <Link to="/apiref">API Reference</Link>.
                </p>
            </>
        );
    }
}

